import { HttpError } from '@magicdoorinc/errors';
import { createLazyResource, createMutation, toast, useLocalization } from '@magicdoorinc/ui';
import { createMemo } from 'solid-js';
import { useAuth } from '~/contexts/auth/AuthContext';
import { createMagicDoorContext } from '~/contexts/utils';
import { propertyManagerPermissionsRepository } from '~/repositories/propertyManagerPermissionsRepository';
import { PM_MENU_ACCESS_KEYS } from '~/utils/constant';

interface PropertyManagerDetailsContextProps {
  managerId: string;
}

export const [PropertyManagerDetailsProvider, usePropertyManagerDetails] = createMagicDoorContext(
  'PropertyManagerDetails',
  (props: PropertyManagerDetailsContextProps) => {
    const { me } = useAuth();
    const { t } = useLocalization();

    const isEditingSelf = createMemo(() => {
      return me()?.id === props.managerId;
    });

    const [menuAccessData, { refetch: refetchMenuAccess }] = createLazyResource(
      () => props.managerId,
      async (managerId) => {
        try {
          return await propertyManagerPermissionsRepository.getMenuAccess(managerId);
        } catch (e) {
          if (e instanceof HttpError && e.getStatusCode() === 404) {
            return { menuAccess: PM_MENU_ACCESS_KEYS };
          }

          console.error(e);
          return { menuAccess: [] };
        }
      }
    );

    const [propertyAccessData, { refetch: refetchPropertyAccess }] = createLazyResource(
      () => props.managerId,
      async (managerId) => {
        return await propertyManagerPermissionsRepository.getPropertyAccess(managerId);
      }
    );

    const menuAccess = createMemo(() => menuAccessData.latest?.menuAccess || []);
    const propertyAccess = createMemo(() => propertyAccessData.latest);

    const updateMenuAccessMutation = createMutation(async (accessList: string[]) => {
      if (isEditingSelf()) {
        throw new Error(t('Cannot edit your own menu access'));
      }

      await propertyManagerPermissionsRepository.updateMenuAccess(props.managerId, { menuAccess: accessList });
      await refetchMenuAccess();
    });

    const updatePropertyAccessMutation = createMutation(
      async ({
        portfolioIds,
        propertyIds,
        allProperties = false,
      }: {
        portfolioIds: string[];
        propertyIds: string[];
        allProperties?: boolean;
      }) => {
        if (isEditingSelf()) {
          throw new Error(t('Cannot edit your own property access'));
        }
        await propertyManagerPermissionsRepository.setPropertyAccessBatch(props.managerId, portfolioIds, propertyIds, allProperties);
        toast.success(t('Property access updated successfully'));
        await refetchPropertyAccess();
      }
    );

    return {
      isEditingSelf,
      menuAccessData,
      menuAccess,
      propertyAccess,
      updateMenuAccessMutation,
      updatePropertyAccessMutation,
      refetchMenuAccess,
    };
  }
);
